import { Dimensions, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import { FilterToSendableItems } from '../panels/FilterToSendableItems';
import { QuantityValueSummary } from '../panels/QuantityValueSummary';
import { SmartSendProvider } from '~/lib/consts/providers';

interface SplitSummary {
  item: SmartSendItemApiModel;
  newItems: SmartSendItemApiModel[];
}

interface CombiningSummary {
  items: SmartSendItemApiModel[];
}

export function GenerateSplitSummary(
  group: SmartSendItemGroup,
  split: number,
  products: string[],
  dims: Dimensions
): SplitSummary {
  const existing: SmartSendItemApiModel = JSON.parse(JSON.stringify(group.Items[0]));
  const newItems = [];

  if (split > 1 && existing.MarketplaceStatus === 'Sold') {
    // Add Count Tag
    existing.Tags.push(`Box 1 of ${split}`);

    // Update First Item Desc
    existing.Description = products[0];
    existing.MarketplaceOrderLines.forEach((x) => {
      const s = QuantityValueSummary(x.Value, split);
      x.Value = s.valueForFirstItem;
      x.Product.Description = products[0];
    });

    for (let index = 1; index < split; index++) {
      // Add Subsequent Boxes
      const newItemRequest: SmartSendItemApiModel = JSON.parse(JSON.stringify(group.Items[0]));
      newItemRequest.Id = null;
      newItemRequest.Source.Id = 'manual';
      newItemRequest.MarketplaceOrderLines.forEach((x) => {
        x.Id = null;
        x.MarketplaceOrderId = null;
        x.MarketplaceOrderLineId = null;
        x.CustomerReference = null;
        x.InternalReference = null;
        x.OrderLink = null;
        x.Source.Id = 'manual';

        const s = QuantityValueSummary(x.Value, split);
        x.Value = s.valueForOtherItems;
      });
      newItemRequest.Tags.push(`Box ${index + 1} of ${split}`);
      newItemRequest.PackageDimensions = { ...dims };
      newItemRequest.Description = products[index];
      newItems.push(newItemRequest);
    }

    return { item: existing, newItems: newItems };
  }
}

export function GenerateDuplicateSummary(
  group: SmartSendItemGroup,
  split: number,
  products: string[],
  dims: Dimensions
): SplitSummary {
  const existing: SmartSendItemApiModel = JSON.parse(JSON.stringify(group.Items[0]));
  const newItems = [];

  if (split > 1 && existing.MarketplaceStatus === 'Sold') {
    for (let index = 1; index < split; index++) {
      // Add Subsequent Boxes
      const newItemRequest: SmartSendItemApiModel = JSON.parse(JSON.stringify(group.Items[0]));
      newItemRequest.Id = null;
      newItemRequest.Source.Id = 'manual';
      newItemRequest.MarketplaceOrderLines.forEach((x) => {
        x.Id = null;
        x.MarketplaceOrderId = null;
        x.MarketplaceOrderLineId = null;
        x.CustomerReference = null;
        x.InternalReference = null;
        x.OrderLink = null;
        x.Source.Id = 'manual';
      });
      newItemRequest.PackageDimensions = { ...dims };
      newItemRequest.Description = products[index];
      newItems.push(newItemRequest);
    }

    return { item: existing, newItems: newItems };
  }
}
export function GenerateQuantityConvertedSummary(group: SmartSendItemGroup): SplitSummary {
  const existing = JSON.parse(JSON.stringify(group.Items[0])) as SmartSendItemApiModel;

  const qty = existing.MarketplaceOrderLines[0].Quantity;
  const s = QuantityValueSummary(existing.MarketplaceOrderLines[0].Value, qty);

  existing.MarketplaceOrderLines.forEach((o) => {
    o.Quantity = 1;
    o.Value = s.valueForFirstItem;
  });

  const newItems = [];

  if (existing.MarketplaceStatus === 'Sold') {
    for (let index = 1; index < qty; index++) {
      // Add Subsequent Boxes
      const newItemRequest = JSON.parse(JSON.stringify(group.Items[0])) as SmartSendItemApiModel;
      newItemRequest.Id = null;
      newItemRequest.Source.Id = 'manual';
      newItemRequest.MarketplaceOrderLines.forEach((x) => {
        x.Id = null;
        x.MarketplaceOrderId = null;
        x.MarketplaceOrderLineId = null;
        x.CustomerReference = null;
        x.InternalReference = null;
        x.OrderLink = null;
        x.Source.Id = 'manual';
        x.Quantity = 1;
        x.Value = s.valueForOtherItems;
      });
      newItemRequest.Tags = newItemRequest.Tags.filter((x) => x.indexOf('Box ') === -1);

      newItems.push(newItemRequest);
    }

    return { item: existing, newItems: newItems };
  }
}

export function GenerateCombiningSummary(group: SmartSendItemGroup, dims: Dimensions): CombiningSummary {
  const req: SmartSendItemApiModel[] = [];

  // New Item will all the parcels
  const newItemRequest: SmartSendItemApiModel = JSON.parse(JSON.stringify(group.Items[0]));
  newItemRequest.Id = null;
  newItemRequest.Source = { Id: 'grouped', Name: 'Grouped', AccountId: SmartSendProvider };
  newItemRequest.MarketplaceOrderLines = [];
  newItemRequest.PackageDimensions = { ...dims };
  newItemRequest.Tags = newItemRequest.Tags.filter((x) => x.indexOf('Box ') === -1);

  // All the empty boxes
  group.Items.forEach((i) => {
    newItemRequest.MarketplaceOrderLines = newItemRequest.MarketplaceOrderLines.concat(
      i.MarketplaceOrderLines.filter(FilterToSendableItems)
    );
    req.push({ ...i, MarketplaceOrderLines: [] });
  });

  return { items: req.concat([newItemRequest]) };
}
